/**
 * Persistent Upload Queue Manager
 * 
 * Manages upload jobs with localStorage persistence for resumable uploads.
 * Handles thousands of images by storing job metadata and file references.
 */

export interface UploadJobFile {
  relativePath: string;
  fileName: string;
  fileSize: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  imageId?: string; // Set after successful upload
  retries: number;
  error?: string;
  uploadedAt?: string; // ISO timestamp
  duplicate?: boolean; // True if this file was a duplicate and skipped
}

export interface UploadJob {
  jobId: string;
  createdAt: string; // ISO timestamp
  updatedAt: string; // ISO timestamp
  status: 'active' | 'paused' | 'completed' | 'failed' | 'cancelled';
  files: UploadJobFile[];
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  folderPath?: string; // For display purposes
  brandId?: string;
  projectId?: string;
  // Metadata for resuming
  directoryHandle?: any; // FileSystemDirectoryHandle (can't be serialized, stored separately)
  useFileSystemAPI: boolean; // Whether we used File System Access API
}

export interface UploadJobProgress {
  jobId: string;
  total: number;
  completed: number;
  failed: number;
  current?: string;
  status: UploadJob['status'];
}

const STORAGE_KEY_PREFIX = 'upload_job_';
const ACTIVE_JOBS_KEY = 'active_upload_jobs';
const MAX_STORAGE_SIZE = 5 * 1024 * 1024; // 5MB limit for localStorage

/**
 * Generate a unique job ID
 */
function generateJobId(): string {
  return `job_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Get storage key for a job
 */
function getJobStorageKey(jobId: string): string {
  return `${STORAGE_KEY_PREFIX}${jobId}`;
}

/**
 * Estimate size of data in bytes
 */
function estimateSize(data: any): number {
  return JSON.stringify(data).length * 2; // Rough estimate (UTF-16)
}

/**
 * Get all active job IDs
 */
export function getActiveJobIds(): string[] {
  try {
    const stored = localStorage.getItem(ACTIVE_JOBS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Failed to get active job IDs:', error);
    return [];
  }
}

/**
 * Save active job ID
 */
function saveActiveJobId(jobId: string): void {
  const activeIds = getActiveJobIds();
  if (!activeIds.includes(jobId)) {
    activeIds.push(jobId);
    localStorage.setItem(ACTIVE_JOBS_KEY, JSON.stringify(activeIds));
  }
}

/**
 * Remove active job ID
 */
function removeActiveJobId(jobId: string): void {
  const activeIds = getActiveJobIds();
  const filtered = activeIds.filter(id => id !== jobId);
  localStorage.setItem(ACTIVE_JOBS_KEY, JSON.stringify(filtered));
}

/**
 * Create a new upload job
 */
export function createUploadJob(
  files: Array<{ file: File; relativePath: string }>,
  options?: {
    brandId?: string;
    projectId?: string;
    folderPath?: string;
    useFileSystemAPI?: boolean;
    directoryHandle?: any;
  }
): UploadJob {
  const jobId = generateJobId();
  const now = new Date().toISOString();
  
  const jobFiles: UploadJobFile[] = files.map(({ file, relativePath }) => ({
    relativePath,
    fileName: file.name,
    fileSize: file.size,
    status: 'pending',
    retries: 0,
  }));

  const job: UploadJob = {
    jobId,
    createdAt: now,
    updatedAt: now,
    status: 'active',
    files: jobFiles,
    totalFiles: jobFiles.length,
    completedFiles: 0,
    failedFiles: 0,
    folderPath: options?.folderPath,
    brandId: options?.brandId,
    projectId: options?.projectId,
    useFileSystemAPI: options?.useFileSystemAPI ?? false,
    // Note: directoryHandle cannot be serialized, must be stored separately if needed
  };

  saveJob(job);
  saveActiveJobId(jobId);
  
  return job;
}

/**
 * Save job to localStorage
 */
export function saveJob(job: UploadJob): void {
  try {
    // Clean up directoryHandle before saving (can't serialize)
    const jobToSave = {
      ...job,
      directoryHandle: undefined, // Remove handle, we'll need to re-request it
    };
    
    const key = getJobStorageKey(job.jobId);
    const data = JSON.stringify(jobToSave);
    
    // Check if we have enough space
    if (estimateSize(data) > MAX_STORAGE_SIZE) {
      console.warn(`Job ${job.jobId} is too large for localStorage, consider cleaning up old jobs`);
    }
    
    localStorage.setItem(key, data);
    
    // Update active jobs list
    if (job.status === 'active' || job.status === 'paused') {
      saveActiveJobId(job.jobId);
    } else {
      removeActiveJobId(job.jobId);
    }
  } catch (error) {
    console.error(`Failed to save job ${job.jobId}:`, error);
    // If quota exceeded, try to clean up old jobs
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      cleanupOldJobs();
      // Retry once
      try {
        localStorage.setItem(getJobStorageKey(job.jobId), JSON.stringify({
          ...job,
          directoryHandle: undefined,
        }));
      } catch (retryError) {
        console.error('Failed to save job after cleanup:', retryError);
      }
    }
  }
}

/**
 * Load job from localStorage
 */
export function loadJob(jobId: string): UploadJob | null {
  try {
    const key = getJobStorageKey(jobId);
    const stored = localStorage.getItem(key);
    if (!stored) {
      return null;
    }
    return JSON.parse(stored) as UploadJob;
  } catch (error) {
    console.error(`Failed to load job ${jobId}:`, error);
    return null;
  }
}

/**
 * Load all active jobs
 */
export function loadAllActiveJobs(): UploadJob[] {
  const jobIds = getActiveJobIds();
  const jobs: UploadJob[] = [];
  
  for (const jobId of jobIds) {
    const job = loadJob(jobId);
    if (job && (job.status === 'active' || job.status === 'paused')) {
      jobs.push(job);
    }
  }
  
  return jobs.sort((a, b) => 
    new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

/**
 * Update file status in a job
 */
export function updateJobFileStatus(
  jobId: string,
  relativePath: string,
  updates: Partial<UploadJobFile>
): void {
  const job = loadJob(jobId);
  if (!job) {
    console.warn(`Job ${jobId} not found`);
    return;
  }
  
  const fileIndex = job.files.findIndex(f => f.relativePath === relativePath);
  if (fileIndex === -1) {
    console.warn(`File ${relativePath} not found in job ${jobId}`);
    return;
  }
  
  const oldStatus = job.files[fileIndex].status;
  const newStatus = updates.status ?? oldStatus;
  
  // Update file
  job.files[fileIndex] = {
    ...job.files[fileIndex],
    ...updates,
    status: newStatus,
  };
  
  // Update counters
  if (oldStatus === 'completed' && newStatus !== 'completed') {
    job.completedFiles--;
  } else if (oldStatus !== 'completed' && newStatus === 'completed') {
    job.completedFiles++;
  }
  
  if (oldStatus === 'failed' && newStatus !== 'failed') {
    job.failedFiles--;
  } else if (oldStatus !== 'failed' && newStatus === 'failed') {
    job.failedFiles++;
  }
  
  // Update job status
  if (job.completedFiles === job.totalFiles) {
    job.status = 'completed';
  } else if (job.status === 'completed' && job.completedFiles < job.totalFiles) {
    job.status = 'active';
  }
  
  job.updatedAt = new Date().toISOString();
  saveJob(job);
}

/**
 * Update job status
 */
export function updateJobStatus(
  jobId: string,
  status: UploadJob['status']
): void {
  const job = loadJob(jobId);
  if (!job) {
    return;
  }
  
  job.status = status;
  job.updatedAt = new Date().toISOString();
  saveJob(job);
}

/**
 * Delete a job
 */
export function deleteJob(jobId: string): void {
  try {
    const key = getJobStorageKey(jobId);
    localStorage.removeItem(key);
    removeActiveJobId(jobId);
  } catch (error) {
    console.error(`Failed to delete job ${jobId}:`, error);
  }
}

/**
 * Clean up old completed/failed jobs (keep last N)
 */
export function cleanupOldJobs(keepLast: number = 10): void {
  const jobIds = getActiveJobIds();
  const allJobs: Array<{ jobId: string; updatedAt: string; status: string }> = [];
  
  // Load all jobs to check their status
  for (const jobId of jobIds) {
    const job = loadJob(jobId);
    if (job) {
      allJobs.push({
        jobId: job.jobId,
        updatedAt: job.updatedAt,
        status: job.status,
      });
    }
  }
  
  // Sort by updatedAt (oldest first)
  allJobs.sort((a, b) => 
    new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
  );
  
  // Delete old completed/failed jobs
  let deleted = 0;
  for (const job of allJobs) {
    if ((job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') && 
        deleted < allJobs.length - keepLast) {
      deleteJob(job.jobId);
      deleted++;
    }
  }
  
  console.log(`Cleaned up ${deleted} old jobs`);
}

/**
 * Get job progress
 */
export function getJobProgress(jobId: string): UploadJobProgress | null {
  const job = loadJob(jobId);
  if (!job) {
    return null;
  }
  
  const currentFile = job.files.find(f => f.status === 'uploading');
  
  return {
    jobId: job.jobId,
    total: job.totalFiles,
    completed: job.completedFiles,
    failed: job.failedFiles,
    current: currentFile?.fileName,
    status: job.status,
  };
}

